/**
 * Login embutido no app — Resource Owner Password Grant direto no Keycloak (realm `storeo`,
 * client `storeo-painel`, `directAccessGrantsEnabled: true` em
 * `infra/keycloak/storeo-realm.json`). Sem redirect: o formulário do próprio painel manda
 * e-mail/senha pro `/token` endpoint. Troca o antigo Authorization Code + PKCE — o redirect
 * pro tema padrão do Keycloak (inglês, sem marca) quebrava a experiência (plano em
 * `~/.claude/plans/mutable-waddling-aurora.md`). `sessionStorage` só — a sessão do painel cai
 * com a aba (ADR 0019 §1).
 */

const BASE = import.meta.env.VITE_KEYCLOAK_BASE_URL ?? 'http://localhost:8081';
const REALM = import.meta.env.VITE_KEYCLOAK_REALM ?? 'storeo';
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT ?? 'storeo-painel';

const ISSUER = `${BASE}/realms/${REALM}`;
const TOKEN_KEY = 'storeo:painel:token';

export async function login(email: string, password: string): Promise<void> {
  const res = await fetch(`${ISSUER}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'password',
      client_id: CLIENT_ID,
      username: email,
      password,
      scope: 'openid email',
    }),
  });
  if (!res.ok) throw new Error('E-mail ou senha incorretos');
  const body = (await res.json()) as { access_token: string };
  sessionStorage.setItem(TOKEN_KEY, body.access_token);
}

export function currentToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function logout(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

export interface TokenClaims {
  name?: string;
  email?: string;
  exp?: number;
  realm_access?: { roles?: string[] };
}

/** Só pra UI (nome/e-mail/expiração) — a validação de verdade é sempre do lado da API. */
export function decodeClaims(token: string): TokenClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as TokenClaims;
  } catch {
    return null;
  }
}

export function isExpired(claims: TokenClaims | null): boolean {
  if (!claims?.exp) return true;
  return Date.now() >= claims.exp * 1000;
}
